import { ensureSubscriptionActionAllowed } from '../../utils/subscriptionActionGuard';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Typography,
  Tooltip,
  message,
  Dropdown,
  MenuProps,
  theme,
} from "antd";
import {
  EyeOutlined,
  DownloadOutlined,
  PrinterOutlined,
  FileTextOutlined,
  SearchOutlined,
  FilterOutlined,
  CalendarOutlined,
  TeamOutlined,
  MoneyCollectOutlined,
  ExportOutlined,
  MoreOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import PayslipDetailsModal from "../payroll-processing/components/PayslipDetailsModal";
import TemplateSelector from "./TemplateSelector";
import { formatCurrency, formatDate } from "../payroll-processing/utils";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useSelection } from '../../SelectionContext';
import { getAuthorizedCompanyContext } from '../../utils/companyContext';
import API_CONFIG from '../../config/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface PayslipRecord {
  id: number;
  period_id: number;
  employee_id: number;
  period_name: string;
  period_start_date: string;
  period_end_date: string;
  employee_number: string;
  employee_first_name: string;
  employee_last_name: string;
  department_name: string;
  position_name: string;
  gross_salary: number;
  net_salary: number;
  total_deductions: number;
  status: 'completed' | 'validated' | 'paid' | 'error';
  calculation_date: string;
  validation_date?: string;
  payment_date?: string;
}

interface PayslipFilters {
  search?: string;
  period_id?: number;
  department?: string;
  status?: string;
  date_range?: [string, string];
}

interface PayslipStats {
  total_payslips: number;
  total_gross_amount: number;
  total_net_amount: number;
  validated_count: number;
  paid_count: number;
  pending_count: number;
}

const PayslipList: React.FC = () => {
  const { token, hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const { token: themeToken } = theme.useToken();
  const requestVersion = useRef(0);
  const loadController = useRef<AbortController | null>(null);
  const contextKey = `${token}:${selectedCompany}`;
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  const canPrint = hasPermission('Ressources Humaines.Paie.Bulletins.print');
  const canView = hasPermission('Ressources Humaines.Paie.Bulletins.view');
  const canExport = canView;
  const canManageTemplates = ['view', 'create', 'edit', 'duplicate'].some(action => hasPermission(`Ressources Humaines.Templates.Bulletins.${action}`));
  const requestHeaders = useCallback(() => {
    const companyId = selectedCompany ? getAuthorizedCompanyContext() : null;
    if (selectedCompany && !companyId) throw new Error('Sélectionnez une entreprise accessible pour charger les bulletins et leurs modèles.');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
      ...(companyId ? { 'X-Company-Id': String(companyId) } : {}) };
  }, [token, selectedCompany]);
  const [loading, setLoading] = useState(false);
  const [payslips, setPayslips] = useState<PayslipRecord[]>([]);
  const [stats, setStats] = useState<PayslipStats | null>(null);
  const [filters, setFilters] = useState<PayslipFilters>({});
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { current: currentPage, pageSize } = pagination;

  // Modal states
  const [selectedPayslip, setSelectedPayslip] = useState<PayslipRecord | null>(null);
  const [showPayslipDetails, setShowPayslipDetails] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateSelectorPayslips, setTemplateSelectorPayslips] = useState<number[]>([]);
  
  // Selection states
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Dropdown options
  const [periods, setPeriods] = useState<Array<{id: number, name: string}>>([]);
  const [departments, setDepartments] = useState<Array<{name: string}>>([]);

  const loadPayslips = useCallback(async () => {
    if (!token) return;
    
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    const version = ++requestVersion.current;
    setLoading(true);
    setPayslips([]);
    setStats(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (filters.search) params.append('search', filters.search);
      if (filters.period_id) params.append('period_id', filters.period_id.toString());
      if (filters.department) params.append('department', filters.department);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_range) {
        params.append('start_date', filters.date_range[0]);
        params.append('end_date', filters.date_range[1]);
      }

      const response = await fetch(API_CONFIG.url(`/api/hr/payslips?${params}`), {
        headers: requestHeaders(), signal: controller.signal
      });

      const data = await response.json();
      if (version !== requestVersion.current || controller.signal.aborted) return;
      if (response.ok && data.success) {
        setPayslips(data.data || []);
        setStats(data.stats || {
          total_payslips: 0,
          total_gross_amount: 0,
          total_net_amount: 0,
          validated_count: 0,
          paid_count: 0,
          pending_count: 0,
        });
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
        }));
      } else {
        message.error(data.error || "Erreur lors du chargement des bulletins");
        // Reset data on error
        setPayslips([]);
        setStats({
          total_payslips: 0,
          total_gross_amount: 0,
          total_net_amount: 0,
          validated_count: 0,
          paid_count: 0,
          pending_count: 0,
        });
      }
    } catch (error) {
      if (version !== requestVersion.current || controller.signal.aborted) return;
      message.error("Erreur lors du chargement des bulletins");
      // Reset data on error
      setPayslips([]);
      setStats({
        total_payslips: 0,
        total_gross_amount: 0,
        total_net_amount: 0,
        validated_count: 0,
        paid_count: 0,
        pending_count: 0,
      });
    } finally {
      if (version === requestVersion.current && !controller.signal.aborted) setLoading(false);
    }
  }, [token, currentPage, pageSize, filters, requestHeaders]);

  useEffect(() => {
    setSelectedRowKeys([]);
    setExportLoading(false);
    setSelectedPayslip(null);
    setShowPayslipDetails(false);
    setShowTemplateSelector(false);
    setTemplateSelectorPayslips([]);
    setPeriods([]);
    setDepartments([]);
    setFilters({});
    setSearchInput('');
    setPagination(previous => ({ ...previous, current: 1, total: 0 }));
    const controller = new AbortController();
    if (token) {
      const readOptions = async (path: string) => {
        const response = await fetch(API_CONFIG.url(path), { headers: requestHeaders(), signal: controller.signal });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.error || 'Erreur lors du chargement des filtres');
        return data.data || [];
      };
      Promise.all([
        readOptions('/api/hr/payroll-processing/periods'), readOptions('/api/hr/departments')
      ]).then(([periodData, departmentData]) => {
        if (controller.signal.aborted) return;
        setPeriods(periodData.map((period: any) => ({ id: period.id, name: period.name || period.period_name })));
        setDepartments(departmentData);
      }).catch(() => {
        if (!controller.signal.aborted) message.error('Impossible de charger les filtres de l’entreprise');
      });
    }
    return () => controller.abort();
  }, [token, selectedCompany, requestHeaders]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Effect for loading payslips when filters or pagination change
  useEffect(() => {
    if (token) {
      loadPayslips();
    }
    return () => { requestVersion.current += 1; loadController.current?.abort(); };
  }, [loadPayslips, token]);

  const handleViewPayslip = useCallback((record: PayslipRecord) => {
    setSelectedPayslip(record);
    setShowPayslipDetails(true);
  }, []);

  const handleDownloadPDF = useCallback((record: PayslipRecord) => {
    if (!canPrint && !canView) return;
    setTemplateSelectorPayslips([record.id]);
    setShowTemplateSelector(true);
  }, [canPrint, canView]);

  const handleBulkDownloadPDF = useCallback(() => {
    if (!canPrint) return;
    if (selectedRowKeys.length === 0) {
      message.warning("Veuillez sélectionner au moins un bulletin à télécharger");
      return;
    }
    setTemplateSelectorPayslips(selectedRowKeys as number[]);
    setShowTemplateSelector(true);
  }, [selectedRowKeys, canPrint]);

  const handleTemplateDownloadComplete = useCallback(() => {
    setSelectedRowKeys([]); // Clear selection after download
  }, []);

  const handleExportSelection = useCallback(async () => {
    if (!canExport || exportLoading) return;
    const requestContext = currentContext.current;
    if (!(await ensureSubscriptionActionAllowed('data_export')) || currentContext.current !== requestContext) return;
    if (selectedRowKeys.length === 0) {
      message.warning("Veuillez sélectionner au moins un bulletin à exporter");
      return;
    }

    setExportLoading(true);
    try {
      const response = await fetch(API_CONFIG.url('/api/hr/payslips/export'), {
        method: 'POST',
        headers: requestHeaders(),
        body: JSON.stringify({ 
          payslip_ids: selectedRowKeys,
          export_format: 'excel' // Par défaut Excel, peut être paramétrable
        }),
      });

      if (currentContext.current !== requestContext) return;
      if (response.ok) {
        // Télécharger le fichier
        const blob = await response.blob();
        if (currentContext.current !== requestContext) return;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulletins_paie_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        message.success(`${selectedRowKeys.length} bulletin(s) exporté(s) avec succès`);
        setSelectedRowKeys([]); // Réinitialiser la sélection
      } else {
        const errorData = await response.json();
        message.error(errorData.error || "Erreur lors de l'export");
      }
    } catch (error) {
      if (currentContext.current !== requestContext) return;
      message.error("Erreur lors de l'export des bulletins");
    } finally {
      if (currentContext.current === requestContext) setExportLoading(false);
    }
  }, [selectedRowKeys, requestHeaders, canExport, exportLoading]);

  const getStatusTag = useCallback((status: string) => {
    const statusConfig = {
      completed: { color: 'processing', text: 'Calculé' },
      validated: { color: 'success', text: 'Validé' },
      paid: { color: 'success', text: 'Payé' },
      error: { color: 'error', text: 'Erreur' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.completed;
    return <Tag color={config.color}>{config.text}</Tag>;
  }, []);

  const getActionMenuItems = useCallback((record: PayslipRecord): MenuProps['items'] => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'Voir le détail',
      onClick: () => handleViewPayslip(record),
    },
    {
      key: 'preview',
      icon: <EyeOutlined />,
      label: 'Aperçu du bulletin PDF',
      disabled: !canView,
      onClick: () => handleDownloadPDF(record),
    },
    {
      type: 'divider',
    },
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: 'Télécharger PDF',
      disabled: !canPrint,
      onClick: () => handleDownloadPDF(record),
    },
    {
      key: 'print',
      icon: <PrinterOutlined />,
      label: 'PDF pour impression',
      disabled: !canPrint,
      onClick: () => {
        // Open template selector for printing (same as download for now)
        handleDownloadPDF(record);
      },
    },
  ], [handleViewPayslip, handleDownloadPDF, canPrint, canView]);

  const columns: ColumnsType<PayslipRecord> = [
    {
      title: 'Période',
      dataIndex: 'period_name',
      key: 'period_name',
      width: 150,
      render: (text: string, record: PayslipRecord) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatDate(record.period_start_date)} - {formatDate(record.period_end_date)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Employé',
      key: 'employee',
      width: 200,
      render: (_, record: PayslipRecord) => (
        <div>
          <Text strong>
            {record.employee_first_name} {record.employee_last_name}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            N° {record.employee_number}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.department_name} - {record.position_name}
          </Text>
        </div>
      ),
    },
    {
      title: 'Salaire Brut',
      dataIndex: 'gross_salary',
      key: 'gross_salary',
      width: 120,
      align: 'right',
      render: (amount: number) => (
        <Text style={{ color: themeToken.colorSuccess, fontWeight: 'bold' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Déductions',
      dataIndex: 'total_deductions',
      key: 'total_deductions',
      width: 120,
      align: 'right',
      render: (amount: number) => (
        <Text style={{ color: themeToken.colorError }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Salaire Net',
      dataIndex: 'net_salary',
      key: 'net_salary',
      width: 120,
      align: 'right',
      render: (amount: number) => (
        <Text style={{ color: themeToken.colorPrimary, fontWeight: 'bold', fontSize: '14px' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Date Calcul',
      dataIndex: 'calculation_date',
      key: 'calculation_date',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record: PayslipRecord) => (
        <Space size="small">
          <Tooltip title="Voir le détail">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewPayslip(record)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const handleTableChange = useCallback((newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  }, []);

  const handleSearchInput = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  const handleFilterChange = useCallback((key: keyof PayslipFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({});
    setSearchInput('');
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <FileTextOutlined style={{ marginRight: 8, color: themeToken.colorPrimary }} />
          Bulletins de Paie
        </Title>
        <Text type="secondary">
          Consultez, téléchargez et gérez tous les bulletins de paie
        </Text>
        {canManageTemplates && <div style={{ marginTop: 12 }}>
          <Button icon={<SettingOutlined />} onClick={() => {
            setTemplateSelectorPayslips([]);
            setShowTemplateSelector(true);
          }}>Modèles de bulletin</Button>
        </div>}
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Bulletins"
              value={stats?.total_payslips || 0}
              prefix={<FileTextOutlined style={{ color: themeToken.colorPrimary }} />}
              valueStyle={{ color: themeToken.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Masse Salariale Brute"
              value={stats?.total_gross_amount || 0}
              formatter={(value) => formatCurrency(Number(value || 0))}
              prefix={<MoneyCollectOutlined style={{ color: themeToken.colorSuccess }} />}
              valueStyle={{ color: themeToken.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Masse Salariale Nette"
              value={stats?.total_net_amount || 0}
              formatter={(value) => formatCurrency(Number(value || 0))}
              prefix={<TeamOutlined style={{ color: themeToken.colorPrimary }} />}
              valueStyle={{ color: themeToken.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bulletins Validés"
              value={stats?.validated_count || 0}
              suffix={`/ ${stats?.total_payslips || 0}`}
              prefix={<CalendarOutlined style={{ color: themeToken.colorWarning }} />}
              valueStyle={{ color: themeToken.colorWarning }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Rechercher employé..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => handleSearchInput(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              placeholder="Période"
              style={{ width: '100%' }}
              value={filters.period_id}
              onChange={(value) => handleFilterChange('period_id', value)}
              allowClear
            >
              {periods.map(period => (
                <Option key={period.id} value={period.id}>
                  {period.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              placeholder="Département"
              style={{ width: '100%' }}
              value={filters.department}
              onChange={(value) => handleFilterChange('department', value)}
              allowClear
            >
              {departments.map(dept => (
                <Option key={dept.name} value={dept.name}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              placeholder="Statut"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              allowClear
            >
              <Option value="completed">Calculé</Option>
              <Option value="validated">Validé</Option>
              <Option value="paid">Payé</Option>
              <Option value="error">Erreur</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.date_range ? [
                dayjs(filters.date_range[0]),
                dayjs(filters.date_range[1])
              ] : null}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('date_range', [
                    dates[0]?.format('YYYY-MM-DD'),
                    dates[1]?.format('YYYY-MM-DD')
                  ]);
                } else {
                  handleFilterChange('date_range', undefined);
                }
              }}
              placeholder={['Date début', 'Date fin']}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space wrap>
            <Button icon={<FilterOutlined />} onClick={resetFilters}>
              Réinitialiser
            </Button>
            <Button 
              icon={<DownloadOutlined />}
              disabled={selectedRowKeys.length === 0 || !canPrint || loading}
              onClick={handleBulkDownloadPDF}
            >
              PDF la sélection ({selectedRowKeys.length})
            </Button>
            <Button 
              type="primary" 
              icon={<ExportOutlined />}
              loading={exportLoading}
              disabled={selectedRowKeys.length === 0 || !canExport || loading}
              onClick={handleExportSelection}
            >
              Exporter Excel ({selectedRowKeys.length})
            </Button>
          </Space>
        </div>
      </Card>

      {/* Main Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={payslips}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              total > 0 ? `${range[0]}-${range[1]} sur ${total} bulletins` : `0 bulletin`,
          }}
          onChange={handleTableChange}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (newSelectedRowKeys) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              if (selected) {
                // Sélectionner tous les éléments de la page courante
                const allKeys = payslips.map(item => item.id);
                setSelectedRowKeys(prev => {
                  const combined = [...prev, ...allKeys];
                  return Array.from(new Set(combined));
                });
              } else {
                // Désélectionner tous les éléments de la page courante
                const currentPageKeys = payslips.map(item => item.id);
                setSelectedRowKeys(prev => prev.filter(key => !currentPageKeys.includes(key as number)));
              }
            },
            getCheckboxProps: (record) => ({
              name: record.employee_first_name + ' ' + record.employee_last_name,
            }),
          }}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: loading ? 'Chargement...' : 'Aucun bulletin de paie trouvé pour les critères sélectionnés'
          }}
        />
      </Card>

      {/* Payslip Details Modal */}
      <PayslipDetailsModal
        visible={showPayslipDetails}
        employeeId={selectedPayslip?.employee_id || null}
        periodId={selectedPayslip?.period_id || null}
        onCancel={() => {
          setShowPayslipDetails(false);
          setSelectedPayslip(null);
        }}
      />

      {/* Template Selector Modal */}
      <TemplateSelector
        visible={showTemplateSelector}
        payslipIds={templateSelectorPayslips}
        isBulk={templateSelectorPayslips.length > 1}
        onCancel={() => {
          setShowTemplateSelector(false);
          setTemplateSelectorPayslips([]);
        }}
        onDownloadComplete={handleTemplateDownloadComplete}
      />
    </div>
  );
};

export default PayslipList;
